import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  getIdToken,
  getMultiFactorResolver,
  multiFactor,
  TotpMultiFactorGenerator,
  sendPasswordResetEmail
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
export const db = getFirestore(app);
const AuthContext = createContext();

// Where the Decode platform lives for this build of the Site.
const DECODE_ORIGIN =
  process.env.REACT_APP_FIREBASE_PROJECT_ID === 'staging-site-432914'
    ? 'http://localhost:3000'
    : 'https://insurance.bimanalytics.ai';
const TOKEN_ENDPOINT = `https://europe-west2-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/get_custom_tokens`;
// How long to wait for get_custom_tokens before giving up.
const TOKEN_FETCH_TIMEOUT_MS = 20000;
// How long to keep offering the tokens to the Decode tab. The tab cannot
// receive anything until its bundle has downloaded, which on a slow connection
// can take well over half a minute; resending is harmless.
const TOKEN_DELIVERY_CAP_MS = 120000;
const TOKEN_DELIVERY_INTERVAL_MS = 250;

// An error in the Site -> Decode launch that carries a message fit for the user.
class PlatformLaunchError extends Error {
  constructor(message, userMessage) {
    super(message);
    this.name = 'PlatformLaunchError';
    this.userMessage = userMessage;
  }
}

// Posts the tokens to the Decode tab until Decode reports the outcome of
// signing in with them, the tab closes, or the cap elapses. An onload handler
// set on a cross-origin window never fires, so polling is the only way to
// know when the tab is ready. Decode acknowledges only once both of its
// sign-ins have succeeded, and reports a failure otherwise. Resolves with
// 'acknowledged' or 'closed'; rejects on a failure or on the cap.
function deliverTokens(targetWindow, message) {
  return new Promise((resolve, reject) => {
    let retryTimer = null;
    let capTimer = null;
    const stop = () => {
      clearInterval(retryTimer);
      clearTimeout(capTimer);
      window.removeEventListener('message', receiveReply);
    };
    const receiveReply = (event) => {
      if (event.origin !== DECODE_ORIGIN) return;
      if (event.source !== targetWindow) return;
      const type = event.data && event.data.type;
      if (type === 'decode-bridge-ack') {
        stop();
        resolve('acknowledged');
      } else if (type === 'decode-bridge-failed') {
        stop();
        reject(new PlatformLaunchError(
          'Decode could not sign in with the tokens',
          'Decode could not complete your sign-in. Close the Decode tab and try again.'
        ));
      }
    };
    const send = () => {
      if (targetWindow.closed) {
        stop();
        resolve('closed');
        return;
      }
      targetWindow.postMessage(message, DECODE_ORIGIN);
    };
    window.addEventListener('message', receiveReply, false);
    capTimer = setTimeout(() => {
      stop();
      reject(new PlatformLaunchError(
        'Decode did not acknowledge the tokens',
        'Decode did not confirm your sign-in. If the Decode tab is still loading, give it a moment; otherwise close it and try again.'
      ));
    }, TOKEN_DELIVERY_CAP_MS);
    send();
    retryTimer = setInterval(send, TOKEN_DELIVERY_INTERVAL_MS);
  });
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userClaims, setUserClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  // Progress of the "open Decode" action: idle | opening | error. Read by the
  // Header to disable the button while a launch is in flight and to surface
  // failures, which previously went only to the console.
  const [platformLaunch, setPlatformLaunch] = useState({ status: 'idle', error: null });
  // Ref rather than state so a second click in the same tick is refused.
  const platformLaunchRef = useRef(false);

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setMfaResolver(null);
      setMfaRequired(false);
      const token = await getIdToken(userCredential.user);
      return { token, mfaRequired: false };
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        setMfaRequired(true);
        return { token: null, mfaRequired: true };
      }
      throw error;
    }
  }

  async function completeMfaSignIn(otpCode) {
    if (!mfaResolver) throw new Error('No MFA resolver available');
    const totpHint = mfaResolver.hints.find(h => h.factorId === 'totp');
    if (!totpHint) throw new Error('No TOTP factor enrolled');
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, otpCode);
    const userCredential = await mfaResolver.resolveSignIn(assertion);
    setMfaResolver(null);
    setMfaRequired(false);
    const token = await getIdToken(userCredential.user);
    return token;
  }

  async function getMfaEnrollmentStatus() {
    // Use auth.currentUser directly to avoid race with React state updates.
    // Reload from server to guarantee we read authoritative mfaInfo rather
    // than a stale SDK cache.
    const user = auth.currentUser;
    if (!user) return false;
    await user.reload();
    const enrolled = multiFactor(user).enrolledFactors.length > 0;
    setMfaEnrolled(enrolled);
    return enrolled;
  }

  async function startMfaEnrollment() {
    if (!currentUser) throw new Error('User not authenticated');
    const session = await multiFactor(currentUser).getSession();
    const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
    return totpSecret;
  }

  async function finalizeMfaEnrollment(totpSecret, verificationCode) {
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, verificationCode);
    await multiFactor(currentUser).enroll(assertion, 'Authenticator App');
    setMfaEnrolled(true);
  }

  async function unenrollMfa() {
    if (!currentUser) throw new Error('User not authenticated');
    const enrolledFactors = multiFactor(currentUser).enrolledFactors;
    const totpFactor = enrolledFactors.find(f => f.factorId === 'totp');
    if (!totpFactor) throw new Error('No TOTP factor enrolled');
    await multiFactor(currentUser).unenroll(totpFactor);
    setMfaEnrolled(multiFactor(currentUser).enrolledFactors.length > 0);
  }

  async function resetPassword(email) {
    await sendPasswordResetEmail(auth, email);
  }

  function logout() {
    return signOut(auth);
  }

  async function getAuthToken() {
    if (currentUser) {
      return await getIdToken(currentUser, true);
    }
    return null;
  }

  // Requests the Decode custom tokens for the signed-in user. Every failure is
  // thrown as a PlatformLaunchError carrying a message for the user.
  async function fetchPlatformTokens() {
    let idToken;
    try {
      idToken = await getAuthToken();
    } catch (error) {
      throw new PlatformLaunchError(
        `token refresh failed: ${error.message}`,
        'Could not refresh your session. Check your connection and try again.'
      );
    }
    if (!idToken) {
      throw new PlatformLaunchError('no signed-in user', 'Your session has expired. Please log in again.');
    }

    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), TOKEN_FETCH_TIMEOUT_MS);
    let response;
    try {
      response = await fetch(TOKEN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
        signal: controller.signal
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new PlatformLaunchError(
          'get_custom_tokens timed out',
          'Decode took too long to respond. Check your connection and try again.'
        );
      }
      throw new PlatformLaunchError(
        `get_custom_tokens unreachable: ${error.message}`,
        'Could not reach Decode. Check your connection and try again.'
      );
    } finally {
      clearTimeout(abortTimer);
    }

    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }
    if (response.status === 403) {
      throw new PlatformLaunchError(
        `get_custom_tokens refused: ${data.error}`,
        data.error === 'MFA enrollment required'
          ? 'Set up two-factor authentication before opening Decode.'
          : 'Your account does not have access to Decode.'
      );
    }
    if (!response.ok || !data.mainCustomToken || !data.platformCustomToken) {
      throw new PlatformLaunchError(
        `get_custom_tokens failed: ${data.error || `HTTP ${response.status}`}`,
        'Decode sign-in failed. Please try again in a moment.'
      );
    }
    return { mainCustomToken: data.mainCustomToken, platformCustomToken: data.platformCustomToken };
  }

  function navigateToPlatform() {
    if (!currentUser || !mfaEnrolled) {
      setPlatformLaunch({
        status: 'error',
        error: 'Set up two-factor authentication before opening Decode.'
      });
      return;
    }
    if (platformLaunchRef.current) return;

    // Open the tab synchronously, inside the click's user-activation window.
    // Browsers block window.open once roughly five seconds have passed since
    // the click, and the token request can take longer than that on a cold
    // instance or a slow connection. Decode shows "Signing you in" while it
    // waits, and downloads its bundle in parallel with the token minting.
    const platformWindow = window.open(DECODE_ORIGIN, '_blank');
    if (!platformWindow) {
      setPlatformLaunch({
        status: 'error',
        error: 'Your browser blocked the Decode tab. Allow pop-ups for this site and try again.'
      });
      return;
    }

    platformLaunchRef.current = true;
    setPlatformLaunch({ status: 'opening', error: null });

    (async () => {
      let tokens;
      try {
        tokens = await fetchPlatformTokens();
      } catch (error) {
        console.error('Error fetching Decode tokens:', error);
        // Nothing can sign that tab in now, so close it rather than leave it
        // sitting on "Signing you in".
        if (!platformWindow.closed) platformWindow.close();
        platformLaunchRef.current = false;
        setPlatformLaunch({
          status: 'error',
          error: error.userMessage || 'Could not open Decode. Please try again.'
        });
        return;
      }

      try {
        await deliverTokens(platformWindow, tokens);
        setPlatformLaunch({ status: 'idle', error: null });
      } catch (error) {
        console.error('Error delivering Decode tokens:', error);
        setPlatformLaunch({
          status: 'error',
          error: error.userMessage || 'Could not open Decode. Please try again.'
        });
      } finally {
        platformLaunchRef.current = false;
      }
    })();
  }

  function clearPlatformLaunchError() {
    setPlatformLaunch((current) =>
      current.status === 'error' ? { status: 'idle', error: null } : current
    );
  }

  function checkPermission(permission) {
    return userClaims && userClaims.permissions && userClaims.permissions.includes(permission);
  }

  function checkRole(role) {
    return userClaims && userClaims.role === role;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setMfaEnrolled(false);
        setUserClaims(null);
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      // Reload so enrolledFactors reflects the server, not a stale SDK cache.
      // A transient network failure must not leave the site blank, so fall
      // back to the cached user record rather than abandoning the callback.
      try {
        await user.reload();
      } catch (error) {
        console.error('Could not refresh the signed-in user; using cached state:', error);
      }
      // reload() signs the user out if the account was disabled or deleted, and
      // the listener then fires again with null. That later callback owns the
      // state; publishing this stale user would resurrect a signed-out session.
      if (auth.currentUser !== user) return;
      setMfaEnrolled(multiFactor(user).enrolledFactors.length > 0);

      let claims = null;
      try {
        const idTokenResult = await user.getIdTokenResult();
        claims = idTokenResult.claims;
      } catch (error) {
        console.error('Could not read the signed-in user\'s claims:', error);
      }
      if (auth.currentUser !== user) return;
      setUserClaims(claims);
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userClaims,
    loading,
    platformLaunch,
    clearPlatformLaunchError,
    login,
    logout,
    getAuthToken,
    navigateToPlatform,
    checkPermission,
    checkRole,
    mfaRequired,
    mfaEnrolled,
    completeMfaSignIn,
    getMfaEnrollmentStatus,
    startMfaEnrollment,
    finalizeMfaEnrollment,
    unenrollMfa,
    resetPassword
  };

  // Children render immediately so the public site never waits on Firebase.
  // Anything that needs a resolved session (PrivateRoute, the Header's account
  // controls, the login form) reads `loading` and holds off until it clears.
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
